import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import Header from './components/Header'
import Filters from './components/Filters'
import RequestCard from './components/RequestCard'
import Loader from '../../../components/Loader'
import Empty from '../../../components/Empty'
import { ORGANIZER_REQUEST_STATUSES } from '../../../assets/const'
import {
  getEvent,
  getApplications,
  acceptApplication,
  rejectApplication,
} from '../../../actions/organizer'
import { setCurrent } from '../../../store/organizer/slice'
import { formatSubmittedAt } from '../../../utils/smallHelpers'

const BACKEND_TO_UI_STATUS = {
  submitted: ORGANIZER_REQUEST_STATUSES.NEW,
  accepted: ORGANIZER_REQUEST_STATUSES.APPROVED,
  rejected: ORGANIZER_REQUEST_STATUSES.REJECTED,
}


const Requests = () => {
  const { id } = useParams()
  const eventId = Number(id)
  const dispatch = useDispatch()
  const event = useSelector((state) => state.organizer.current)
  const applicants = useSelector((state) => state.organizer.applications)
  const loading = useSelector((state) => state.organizer.loading)

  const [query, setQuery] = useState('')
  const [tab, setTab] = useState(ORGANIZER_REQUEST_STATUSES.NEW)

  const loadApplicants = useCallback(() => {
    dispatch(getApplications(eventId))
  }, [dispatch, eventId])

  useEffect(() => {
    dispatch(getEvent(eventId))
    loadApplicants()
    return () => {
      dispatch(setCurrent(null))
    }
  }, [dispatch, eventId, loadApplicants])

  const handleApprove = useCallback(async (applicationId) => {
    await dispatch(acceptApplication(applicationId))
    loadApplicants()
  }, [dispatch, loadApplicants])

  const handleReject = useCallback(async (applicationId) => {
    await dispatch(rejectApplication(applicationId))
    loadApplicants()
  }, [dispatch, loadApplicants])

  const items = useMemo(() => (applicants || []).map((a) => ({
    id: a.id,
    number: a.id,
    projectName: a.project_title,
    institution: a.participant_org_name || '',
    participants: (a.participants || []).map((p) => ({
      name: p.full_name,
      code: p.section_number || '',
    })),
    supervisors: (a.supervisors || []).map((s) => ({
      name: s.full_name,
      position: s.position || '',
      phone: s.phone || '',
    })),
    equipment: a.equipment || '',
    submittedAt: formatSubmittedAt(a.submitted_at),
    status: BACKEND_TO_UI_STATUS[a.status],
  })), [applicants])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((request) => {
      if (request.status !== tab) return false
      if (!q) return true
      return (
        request.projectName.toLowerCase().includes(q) ||
        request.institution.toLowerCase().includes(q) ||
        request.participants.some((p) => p.name.toLowerCase().includes(q)) ||
        request.supervisors.some((s) => s.name.toLowerCase().includes(q))
      )
    })
  }, [items, query, tab])

  return (
    <section>
      <Header title={event?.name || ''} />
      <Filters
        query={query}
        onQueryChange={setQuery}
        tab={tab}
        onTabChange={setTab}
      />
      {loading ? (
        <Loader size="lg" color="rgb(218 35 41)" />
      ) : filtered.length === 0 ? (
        <Empty />
      ) : (
        <div className="flex flex-col gap-[1.25rem]">
          {filtered.map((request) => (
            <RequestCard
              key={request.id}
              {...request}
              onApprove={() => handleApprove(request.id)}
              onReject={() => handleReject(request.id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default Requests
